/* A labelled multi-line field, with its hint and its error.
 *
 * The same markup, classes and wiring as `TextField` — deliberately, because
 * `design/components/text-field` authors both as one component with a
 * `textarea.field__control` variant. What differs is the element, which is the
 * whole of the difference: a `<textarea>` wraps, grows, and accepts a newline
 * where an `<input>` submits the form instead.
 *
 * Not folded into `TextField` behind a `multiline` prop, because the two have
 * genuinely different prop types — `rows` and `resize` mean nothing to an
 * input, and `type` means nothing to a textarea — and a union of the two
 * would be a component whose accepted props depend on another prop.
 */

import type { ReactNode, TextareaHTMLAttributes } from 'react';
import { forwardRef, useId } from 'react';

export interface TextAreaProps extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'id'> {
  readonly label: string;
  /** Shown before submission, not discovered after it. */
  readonly hint?: ReactNode;
  /** Present means the field is in its error state; absent means it is not. */
  readonly error?: string | undefined;
}

export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(function TextArea(
  { label, hint, error, rows = 3, ...textarea },
  ref,
) {
  const id = useId();
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;

  /* Both, when both exist. A field that describes only its error stops
   * announcing the requirement the moment somebody gets it wrong, which is
   * exactly when they need it. */
  const describedBy = [hint ? hintId : null, error ? errorId : null].filter(Boolean).join(' ');

  return (
    <div className="field">
      <label className="field__label" htmlFor={id}>
        {label}
      </label>
      {/* `rows` is a starting height, not a limit: the CSS sets a minimum and
          allows vertical resize, and nothing anywhere fixes the height around
          text. A translated string runs about 40% longer — `i18n.md`. */}
      <textarea
        {...textarea}
        ref={ref}
        id={id}
        rows={rows}
        className="field__control"
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy || undefined}
      />
      {hint ? (
        <p className="field__hint" id={hintId}>
          {hint}
        </p>
      ) : null}
      {error ? (
        <p className="field__error" id={errorId} role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
});
